import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';

import { api } from 'lib/request/api';
import type {
  InventoryWorkspaceTask,
  InventoryWorkspaceTasksResponse,
} from 'types/api-responses';

export const INVENTORY_WORKSPACE_TASKS_KEY = 'inventory-workspace-tasks';

// ky `api` has a prefixUrl, so the path must NOT start with a leading slash.
const tasksPath = (workspaceId: number) =>
  `inventory_workspaces/${workspaceId}/tasks`;

const tasksKey = (workspaceId: number | null | undefined): QueryKey => [
  INVENTORY_WORKSPACE_TASKS_KEY,
  workspaceId ?? null,
];

// Loads the Inventory Workspace's verification checklist (the full three-task
// catalog, verdicted or not). Gated on the workspace id.
export function useInventoryWorkspaceTasks(
  workspaceId: number | null | undefined
) {
  return useQuery<InventoryWorkspaceTasksResponse>({
    queryKey: tasksKey(workspaceId),
    queryFn: () =>
      api
        .get(tasksPath(workspaceId as number))
        .json<InventoryWorkspaceTasksResponse>(),
    enabled: !!workspaceId,
  });
}

export interface SaveInventoryTaskVars {
  path: string;
  // Present ONLY when the verdict changed: true = correct, false = incorrect,
  // null = revert to unverified. Omit the key to leave the verdict untouched.
  correctnessState?: boolean | null;
  // Present ONLY when the note changed.
  reason?: string;
}

// Best-effort human message from a task-save error (e.g. promoted workspace 422).
export async function parseTaskError(error: unknown): Promise<string | null> {
  if (!(error instanceof HTTPError)) {
    return error instanceof Error ? error.message : null;
  }
  const body = await error.response.json().catch(() => null);
  if (body && typeof body === 'object') {
    const obj = body as {
      message?: string;
      errors?: Array<{ message?: string }>;
    };
    if (obj.errors?.[0]?.message) return obj.errors[0].message;
    if (obj.message) return obj.message;
  }
  return error.message;
}

// Saves a single task's verdict and/or note via PATCH .../tasks/:path (the
// atomic verification save). The body is built by the CALLER via conditional
// keys — `correctnessState` and `reason` are each read by presence, so an absent
// key leaves that facet unchanged and an explicit `null` reverts the verdict.
// On success the returned node replaces its entry in the cached list.
export function useSaveInventoryWorkspaceTask(
  workspaceId: number | null | undefined
) {
  const queryClient = useQueryClient();
  const queryKey = tasksKey(workspaceId);

  return useMutation<InventoryWorkspaceTask, unknown, SaveInventoryTaskVars>({
    mutationFn: ({ path, correctnessState, reason }) => {
      if (!workspaceId) {
        return Promise.reject(new Error('No workspace to save task'));
      }
      // Presence is meaningful, so build the body key by key rather than
      // spreading both fields. Keys are camelCase; rails-boost underscores them.
      const body: Record<string, unknown> = {};
      if (correctnessState !== undefined) {
        body.correctnessState = correctnessState;
      }
      if (reason !== undefined) body.reason = reason;

      return api
        .patch(`${tasksPath(workspaceId)}/${path}`, { json: body })
        .json<InventoryWorkspaceTask>();
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<InventoryWorkspaceTasksResponse>(
        queryKey,
        (prev) => {
          if (!prev) return prev;
          const tasks = prev.tasks.map((t) => {
            return t.path === saved.path ? saved : t;
          });
          return {
            ...prev,
            tasks,
            completed: tasks.filter((t) => t.status !== null).length,
          };
        }
      );
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
